import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link } from "react-router-dom";
import {
  ChevronRight,
  Twitter,
  Linkedin,
  Clock,
  Calendar,
  ArrowUp,
  BookOpen,
  Sparkles,
  Copy,
  Check,
} from "lucide-react";
import Footer from "../components/Footer";

const BlogDetail = ({ resources }) => {
  const { slug } = useParams();
  const [activeSection, setActiveSection] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  // NEW: State and Refs for controlling the FIXED sidebar behavior
  const [tocState, setTocState] = useState("normal"); // 'normal' | 'fixed' | 'bottom'
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const mainGridRef = useRef(null);
  const sidebarContainerRef = useRef(null);

  const resource = resources?.find((r) => r.slug === slug);

  const articleSections = [
    {
      id: "section1",
      title: "The First Challenge: Unclear Agent Inventory and Posture",
      content: `
        <p>As AI adoption accelerates, agents are being deployed across local environments, managed platforms, and custom-built frameworks. Many organizations lack clear answers to basic questions:</p>
        <ul>
          <li>What agents exist?</li>
          <li>What tools do they invoke?</li>
          <li>What permissions do they have?</li>
          <li>Where is access broader than necessary?</li>
        </ul>
        <p>Without this visibility, security teams can struggle to identify over-privileged agents, risky tool connections or configuration gaps, especially before agents run in production. This mirrors early cloud security challenges, where misconfigurations and excessive permissions became widespread due to a lack of centralized visibility.</p>
        <p class="highlight-box">Agentic AI security must start with <strong>visibility and posture management</strong>, which involves identifying all agents, understanding their capabilities, mapping tool access, and defining expected behavior to proactively reduce risk.</p>
      `,
    },
    {
      id: "section2",
      title:
        "The Second Challenge: Limited Visibility Into Runtime Agent Behavior",
      content: `
        <p>Configuration alone does not reflect reality. An agent may be approved and properly configured yet behave in unexpected ways once it begins operating.</p>
        <p>At runtime, agents:</p>
        <ul>
          <li>Process dynamic prompts</li>
          <li>Decide which tools to call</li>
          <li>Execute multi step workflows</li>
          <li>Adapt behavior based on context</li>
        </ul>
        <p>Without runtime visibility, teams cannot validate behavior, troubleshoot issues, or effectively investigate incidents. An agent might chain tool calls in unintended ways or interpret prompts differently than expected, leading to risky outcomes.</p>
        <p>Effective agentic AI security requires continuous monitoring of actual behavior. Capturing prompts, tool calls, workflow steps, and decisions provides clear visibility into how agents behave at runtime. Comparing this activity against approved scopes and policies enables teams to detect anomalies early and understand not only what happened, but also why.</p>
      `,
    },
    {
      id: "section3",
      title: "The Third Challenge: Unsafe Tool Requests and Toxic Agent Flows",
      content: `
        <p>The most serious risks in Agentic AI arise when agents make unsafe tool requests and create toxic execution flows. Agents may invoke tools outside their approved scope, causing chain actions in risky sequences or reliance on data from untrusted or unverified sources. These behaviors can distort decision-making, produce unsafe outputs, and cause agents to act on inputs that should never influence outcomes.</p>
        <p>To mitigate this risk, organizations need real-time policy protection that evaluates agent actions as they occur. This includes identifying out-of-scope or high-risk tool requests, detecting toxic execution paths, and preventing agents from using untrusted data sources.</p>
        <p>Without consistent real-time policy enforcement across agent workflows, enterprises lose control over how agents reason, act, and generate outputs, increasing the likelihood that agents drift beyond their intended scope.</p>
        <div class="insight-box">
          <h4>Why Agentic AI Security Requires a New Approach</h4>
          <p>Traditional security tools weren't built for autonomous agents that make decisions and take actions without human intervention.</p>
        </div>
      `,
    },
    {
      id: "section4",
      title: "How Synthera AI Security Delivers Value",
      content: `
        <p>Agentic AI is not just another application or model. It is an autonomous actor operating across systems, tools, and data. Securing it requires moving beyond static configuration checks to continuous, behavior driven security, with clear identity, runtime insight, and consistent enforcement. Organizations that get this right can maintain Agentic AI trust, control, and accountability.</p>
        <p><strong>Synthera Agentic AI Security</strong> directly addresses these challenges by bringing visibility, control, and consistency to autonomous agents:</p>
        <ul class="feature-list">
          <li>🔍 <strong>Synthera establishes clear agent posture</strong> by identifying agents, mapping tool access and permissions, and defining expected behavior before execution, helping teams uncover over-privileged agents and risky connections early.</li>
          <li>⚡ <strong>Once agents are running, Synthera delivers real-time visibility</strong> into prompts, decisions, and tool activity, creating traceable records that show how actions were initiated and whether agents stayed within approved scopes.</li>
          <li>🛡️ <strong>Synthera also enforces consistent runtime guardrails.</strong> By evaluating prompts, workflow signals, and tool requests, Synthera identifies manipulation attempts and unsafe actions and either blocks them inline or raises alerts based on policy and agent capability, keeping behavior safe and predictable even as conditions change.</li>
        </ul>
      `,
    },
    {
      id: "section5",
      title: "The True Value of Synthera Extends Beyond Agentic AI Alone",
      content: `
        <p>Agentic AI is one part of a broader enterprise AI landscape that includes how employees use AI, the AI applications companies build, and the overall AI security posture. Synthera AI Security brings these together through a single unified solution that provides a holistic view of AI risk, behavior, and control across the organization.</p>
        <p>With Synthera, enterprises do not just secure agents. They secure AI as a system, enabling safe, scalable AI innovation without blind spots.</p>
        <p class="closing statement"><em>Securing Agentic AI is a native part of the Synthera AI Security solution.</em></p>
      `,
    },
  ];

  // FIX: Custom Scroll Logic to force FIXED positioning once past the top section
  useEffect(() => {
    const handleScrollFixed = () => {
      if (!sidebarContainerRef.current || !mainGridRef.current) return;

      const containerRect = sidebarContainerRef.current.getBoundingClientRect();
      const gridRect = mainGridRef.current.getBoundingClientRect();

      const asideElement = sidebarContainerRef.current.querySelector("aside");
      const sidebarHeight = asideElement ? asideElement.offsetHeight : 500;

      const offsetTop = 128; // Represents top-32 placement

      // Force the width to perfectly match the grid column even when 'fixed' pulls it out of flow
      setSidebarWidth(sidebarContainerRef.current.offsetWidth);

      // Check if the article is finished (stops sidebar from hitting footer)
      // 96px accounts for the pb-24 padding on the grid
      if (gridRect.bottom <= offsetTop + sidebarHeight + 96) {
        setTocState("bottom");
      }
      // Check if we have scrolled past the top section -> Make it FIXED
      else if (containerRect.top <= offsetTop) {
        setTocState("fixed");
      }
      // Otherwise, it sits normally at the top
      else {
        setTocState("normal");
      }
    };

    window.addEventListener("scroll", handleScrollFixed);
    window.addEventListener("resize", handleScrollFixed);
    handleScrollFixed(); // Run once on load

    return () => {
      window.removeEventListener("scroll", handleScrollFixed);
      window.removeEventListener("resize", handleScrollFixed);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" },
    );

    const sections = document.querySelectorAll("section[id]");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-3xl shadow-2xl border border-slate-100">
          <BookOpen className="w-10 h-10 text-emerald-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4 text-slate-800">
            Article Not Found
          </h2>
          <Link to="/resources" className="text-emerald-600 font-semibold">
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  const tableOfContents = [
    { id: "section1", title: "1. Unclear Agent Inventory and Posture" },
    {
      id: "section2",
      title: "2. Limited Visibility Into Runtime Agent Behavior",
    },
    { id: "section3", title: "3. Unsafe Tool Requests and Toxic Agent Flows" },
    { id: "section4", title: "4. How Synthera AI Security Delivers Value" },
    { id: "section5", title: "5. The True Value of Synthera AI" },
  ];

  return (
    <>
      <Helmet>
        <title>{resource.title} | Synthera Security</title>

        <meta
          name="description"
          content={
            resource.description ||
            `${resource.title} - Cybersecurity insights, best practices, and expert analysis from Synthera Security.`
          }
        />

        <meta
          name="keywords"
          content="Cybersecurity Blog, AI Security, Zero Trust, PAM, SASE, Threat Intelligence, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta property="og:title" content={resource.title} />

        <meta
          property="og:description"
          content={
            resource.description ||
            `${resource.title} - Cybersecurity insights from Synthera Security.`
          }
        />

        <meta
          property="og:url"
          content={`https://www.syntherasecurity.com/blog/${resource.slug}`}
        />

        <meta property="og:type" content="article" />

        <link
          rel="canonical"
          href={`https://www.syntherasecurity.com/blog/${resource.slug}`}
        />
      </Helmet>

      <div className="min-h-screen bg-white font-sans text-slate-800 relative">
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 z-50 p-4 bg-slate-900 text-white rounded-full shadow-2xl transition-all duration-500 hover:scale-110 hover:bg-[#00a684] ${
            showBackToTop
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none"
          }`}
        >
          <ArrowUp className="w-6 h-6" />
        </button>

        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 lg:pt-40">
          <div className="absolute top-0 left-0 right-0 h-20 bg-[#032d28] z-0  rounded-b-[1.5rem]"></div>

          {/* Header Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-7">
              <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] mb-8">
                <Link to="/" className="text-[#00a684] no-underline">
                  Home
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <Link to="/resources" className="text-[#00a684] no-underline">
                  Resources
                </Link>
                <ChevronRight size={12} className="text-gray-300" />
                <span className="text-[#00a684]">Blog</span>
              </nav>

              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100">
                  <Calendar size={14} /> {resource.date || "March 26, 2026"}
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                  <Clock size={14} /> {resource.readTime || "5m read"}
                </div>
              </div>

              <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-8 tracking-tight">
                {resource.title}
              </h1>

              <div className="flex items-center gap-2 mt-6">
                <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center font-bold text-white shadow-lg text-sm">
                  SA
                </div>

                <div className="leading-tight">
                  <p className="text-base font-bold text-slate-800 mb-0">
                    Shoaib Arif
                  </p>

                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-[0.12em] mt-0">
                    Technical Consulting Engineer
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-[2rem] blur opacity-20 transition duration-500"></div>
                <div className="relative overflow-hidden rounded-[2rem] shadow-2xl">
                  <img
                    src={resource.imageUrl}
                    alt={resource.title}
                    className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Content Layout - ADDED ref={mainGridRef} and relative positioning */}
          <div
            ref={mainGridRef}
            className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-24 items-start relative"
          >
            {/* FIXED LEFT SIDEBAR HANDLED BY JS */}
            <div
              ref={sidebarContainerRef}
              className="lg:col-span-3 hidden lg:block relative h-full"
            >
              <aside
                style={{
                  width: tocState !== "normal" ? `${sidebarWidth}px` : "auto",
                }}
                className={`
                ${tocState === "fixed" ? "fixed top-32" : ""}
                ${tocState === "bottom" ? "absolute bottom-24" : ""}
                ${tocState === "normal" ? "relative" : ""}
                space-y-8 max-h-[calc(100vh-10rem)] overflow-y-auto no-scrollbar
              `}
              >
                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] mb-6 text-slate-400 border-b pb-4 ">
                    Table of Contents
                  </h4>
                  <ul className="space-y-4">
                    {tableOfContents.map((item) => {
                      const isActive = activeSection === item.id;
                      return (
                        <li key={item.id} className="toc-item">
                          <a
                            href={`#${item.id}`}
                            className={`text-sm font-bold leading-snug block no-underline transition-all ${
                              isActive
                                ? "text-emerald-600 translate-x-1"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            {item.title}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
                  <p className="text-[11px] font-black uppercase tracking-[0.3em] mb-4 text-slate-400">
                    Share Insight
                  </p>
                  <div className="flex gap-3">
                    <button className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all">
                      <Twitter size={18} className="mx-auto" />
                    </button>
                    <a
                      href=" "
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      <Linkedin size={18} className="mx-auto" />
                    </a>
                    <button
                      onClick={handleCopyLink}
                      className="flex-1 p-3 bg-slate-50 rounded-xl hover:bg-emerald-50 hover:text-emerald-600 transition-all"
                    >
                      {copied ? (
                        <Check size={18} className="mx-auto" />
                      ) : (
                        <Copy size={18} className="mx-auto" />
                      )}
                    </button>
                  </div>
                </div>
              </aside>
            </div>

            {/* MAIN ARTICLE AREA (SCROLLS NORMALLY) */}
            <div className="lg:col-span-9 max-w-3xl" ref={contentRef}>
              <div className="prose prose-lg max-w-none text-slate-600 leading-relaxed blog-content">
                <div className="mb-12">
                  <p className="text-xl leading-relaxed text-slate-800 font-medium mb-8">
                    Agentic AI is quickly transitioning from experimentation to
                    production. Enterprises are deploying AI agents to interpret
                    goals, decide what actions to take, interact with business
                    tools and APIs, and execute those actions autonomously, with
                    limited or no human oversight.
                  </p>
                  <p className="mb-10 text-lg leading-relaxed">
                    Traditional AI security focuses on models and data. Agentic
                    AI introduces something new: autonomous behavior. Without
                    the right controls, organizations quickly lose track of what
                    agents exist, what they can do, and whether they are
                    behaving as intended.
                  </p>

                  <div className="mb-16">
                    <h3 className="text-xl font-extrabold text-[#032d28] mb-6 flex items-center gap-2 uppercase tracking-wide">
                      <Sparkles size={22} className="text-[#00a684]" /> To
                      secure agentic AI effectively, address three challenges:
                    </h3>
                    <ul className="space-y-4 text-slate-700 dynamic-list">
                      <li className="flex items-start gap-3">
                        <span className="font-black text-[#00a684]">1.</span>
                        <span className="font-bold text-lg">
                          Visibility and Posture
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="font-black text-[#00a684]">2.</span>
                        <span className="font-bold text-lg">
                          Runtime Behavior
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="font-black text-[#00a684]">3.</span>
                        <span className="font-bold text-lg">
                          Guardrails and Controls
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-slate-900 rounded-3xl p-8 mb-12 grid grid-cols-1 md:grid-cols-2 gap-8 text-white">
                  <div className="border-l border-emerald-500/30 pl-6">
                    <p className="text-sm opacity-70 uppercase tracking-widest mb-2">
                      McKinsey Global Survey
                    </p>
                    <p className="text-2xl font-bold">
                      <span className="text-emerald-400">88%</span> of
                      organizations report regular AI use in business.
                    </p>
                  </div>
                  <div className="border-l border-emerald-500/30 pl-6">
                    <p className="text-sm opacity-70 uppercase tracking-widest mb-2">
                      Gartner Prediction
                    </p>
                    <p className="text-2xl font-bold">
                      <span className="text-emerald-400">40%</span> of apps will
                      embed AI agents by 2026.
                    </p>
                  </div>
                </div>

                {articleSections.map((section, index) => (
                  <section
                    key={section.id}
                    id={section.id}
                    className="scroll-mt-32 mb-16 pt-8 border-t border-slate-100 first:border-0"
                  >
                    <div className="flex items-center gap-4 mb-8">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center font-bold text-emerald-600 text-xl">
                        {index + 1}
                      </div>
                      <h2 className="text-3xl font-bold text-slate-800 m-0">
                        {section.title}
                      </h2>
                    </div>
                    <div
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}

                <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-10 text-white shadow-xl text-center">
                  <h3 className="text-white text-2xl font-bold mb-4">
                    The Synthera Edge
                  </h3>
                  <p className="text-emerald-50 text-lg mb-0">
                    With Synthera, enterprises secure AI as a system, enabling
                    safe, scalable AI innovation without blind spots. Securing
                    Agentic AI is a native part of the Synthera AI Security
                    solution.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />

        <style>{`
        .blog-content h2 { margin-top: 0; color: #0f172a; font-weight: 800; }
        .blog-content p { margin-bottom: 1.75rem; font-size: 1.125rem; line-height: 1.85; }
        
        .blog-content section ul { list-style-type: none; padding-left: 0; margin-bottom: 2rem; }
        .blog-content section li { position: relative; padding-left: 1.75rem; margin-bottom: 0.75rem; font-weight: 500; }
        .blog-content section li::before {
          content: "•";
          color: #10b981;
          font-weight: bold;
          position: absolute;
          left: 0;
          font-size: 1.5rem;
          line-height: 1.2;
        }

        .toc-item::before, .dynamic-list li::before { display: none !important; }

        .highlight-box {
          background: #f0faf8;
          border-left: 4px solid #10b981;
          padding: 2rem;
          border-radius: 0 1.5rem 1.5rem 0;
          margin: 2.5rem 0;
          color: #064e3b;
        }
        .insight-box {
          background: #0f172a;
          color: white;
          padding: 2.5rem;
          border-radius: 1.5rem;
          margin: 3rem 0;
        }
        .insight-box h4 { color: #10b981; margin-bottom: 1rem; font-weight: 800; }
        html { scroll-behavior: smooth; }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      </div>
    </>
  );
};

export default BlogDetail;
